import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { PlusCircle, Edit, Trash2, Eye, Clock, User } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import './KnowledgeEditorPage.css';

const STATUS_LABELS = {
    draft:     'Черновик',
    review:    'На проверке',
    revision:  'На доработке',
    published: 'Опубликован',
};

const STATUS_COLORS = {
    draft:     'status-draft',
    review:    'status-review',
    revision:  'status-revision',
    published: 'status-published',
};

function formatDateTime(dt) {
    if (!dt) return '';
    const raw = String(dt).trim();
    const withZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(raw)
        ? raw
        : `${raw.replace(' ', 'T')}Z`;
    return new Date(withZone).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

const KnowledgeEditorPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const isAdmin = user?.role === 'admin';

    const { data: articles = [], isLoading } = useQuery(
        'knowledgeArticles',
        async () => {
            const res = await axios.get(`${API_BASE_URL}/knowledge`, {
                withCredentials: true
            });
            return res.data;
        }
    );

    const deleteMutation = useMutation(
        async (id) => {
            await axios.delete(`${API_BASE_URL}/knowledge/${id}`, { withCredentials: true });
        },
        {
            onSuccess: () => {
                toast.success('Статья удалена');
                queryClient.invalidateQueries('knowledgeArticles');
            },
            onError: (err) => {
                toast.error(err.response?.data?.message || 'Ошибка удаления');
            }
        }
    );

    const handleDelete = (e, id, title) => {
        e.stopPropagation();
        if (!window.confirm(`Удалить статью «${title}»?`)) return;
        deleteMutation.mutate(id);
    };

    const canEdit = (article) => {
        if (isAdmin) return true;
        return article.status !== 'published';
    };

    return (
        <div className="knowledge-editor-page">
            <div className="ke-header">
                <div>
                    <h1>Редактор знаний</h1>
                    <p className="ke-subtitle">
                        {isAdmin ? 'Все статьи базы знаний' : 'Мои статьи'}
                    </p>
                </div>
                <button
                    className="ke-new-btn"
                    onClick={() => navigate('/knowledge/editor/new')}
                >
                    <PlusCircle size={18} />
                    Создать статью
                </button>
            </div>

            {isLoading ? (
                <div className="ke-loading"><div className="spinner" /></div>
            ) : articles.length === 0 ? (
                <div className="ke-empty">
                    <p>Статей пока нет</p>
                    <button className="ke-new-btn" onClick={() => navigate('/knowledge/editor/new')}>
                        <PlusCircle size={18} />
                        Создать первую статью
                    </button>
                </div>
            ) : (
                <div className="ke-table-wrap">
                    <table className="ke-table">
                        <thead>
                            <tr>
                                <th>Заголовок</th>
                                {isAdmin && <th>Автор</th>}
                                <th>Статус</th>
                                <th>Изменено</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {articles.map(article => (
                                <tr key={article.id} className="ke-row">
                                    <td className="ke-cell-title">
                                        <span
                                            className="ke-title-link"
                                            onClick={() => navigate(`/knowledge/${article.id}`)}
                                        >
                                            {article.title}
                                        </span>
                                    </td>
                                    {isAdmin && (
                                        <td className="ke-cell-author">
                                            <span className="ke-author">
                                                <User size={13} />
                                                {article.author_name}
                                            </span>
                                        </td>
                                    )}
                                    <td>
                                        <span className={`ke-status ${STATUS_COLORS[article.status]}`}>
                                            {STATUS_LABELS[article.status]}
                                        </span>
                                    </td>
                                    <td className="ke-cell-date">
                                        <span className="ke-date">
                                            <Clock size={12} />
                                            {formatDateTime(article.updated_at)}
                                        </span>
                                    </td>
                                    <td className="ke-cell-actions">
                                        <button
                                            className="ke-action-btn ke-view-btn"
                                            onClick={() => navigate(`/knowledge/${article.id}`)}
                                            title="Просмотр"
                                        >
                                            <Eye size={15} />
                                        </button>
                                        {canEdit(article) && (
                                            <button
                                                className="ke-action-btn ke-edit-btn"
                                                onClick={() => navigate(`/knowledge/editor/edit/${article.id}`)}
                                                title="Редактировать"
                                            >
                                                <Edit size={15} />
                                            </button>
                                        )}
                                        {(isAdmin || article.status !== 'published') && (
                                            <button
                                                className="ke-action-btn ke-del-btn"
                                                onClick={(e) => handleDelete(e, article.id, article.title)}
                                                title="Удалить"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default KnowledgeEditorPage;
